import { useEffect, useState } from "react";
import { CreditCard, Plus, RefreshCw, Loader2, CheckCircle2, XCircle, Clock, ExternalLink, Wallet, Copy, RotateCcw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  PENDING:    { color: "#f5a623", bg: "#f5a62322", label: "⏳ In attesa", icon: Clock },
  PAID:       { color: "#00c853", bg: "#00c85322", label: "✅ Pagato", icon: CheckCircle2 },
  SUCCESSFUL: { color: "#00c853", bg: "#00c85322", label: "✅ Pagato", icon: CheckCircle2 },
  FAILED:     { color: "#d32f2f", bg: "#d32f2f22", label: "❌ Fallito", icon: XCircle },
  EXPIRED:    { color: "#888", bg: "#88888822", label: "⏰ Scaduto", icon: XCircle },
  REFUNDED:   { color: "#9c27b0", bg: "#9c27b022", label: "↩️ Rimborsato", icon: RotateCcw },
};

export default function SumupPayments() {
  const [status, setStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount: "", description: "", payment_type: "generico"
  });

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/sumup/status"),
        api.get("/sumup/payments"),
      ]);
      setStatus(s.data);
      setPayments(p.data.items || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createPayment = async () => {
    if (!form.amount || parseFloat(form.amount) < 0.5) {
      toast.error("Importo minimo €0.50");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/sumup/checkout", {
        amount: parseFloat(form.amount),
        currency: "EUR",
        description: form.description || "Pagamento Fabrirent",
        payment_type: form.payment_type,
      });
      toast.success(`Checkout creato: ${r.data.checkout_reference}`);
      setShowNew(false);
      setForm({ amount: "", description: "", payment_type: "generico" });
      await load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const refreshOne = async (id) => {
    try {
      await api.get(`/sumup/checkout/${id}`);
      toast.success("Stato aggiornato");
      await load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const refund = async (id) => {
    if (!window.confirm("Eseguire il rimborso totale di questo pagamento?")) return;
    try {
      await api.post(`/sumup/payments/${id}/refund`, {});
      toast.success("Rimborso eseguito");
      await load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato`);
  };

  const totalIncassato = payments
    .filter(p => ["PAID","SUCCESSFUL"].includes(p.status))
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPending = payments
    .filter(p => p.status === "PENDING")
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="sumup-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Wallet className="text-blue-500"/> SumUp Pagamenti
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Checkout LIVE · Cauzione · Saldi · Storico transazioni · Rimborsi
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 border border-zinc-700" data-testid="reload">
            <RefreshCw size={16}/> Aggiorna
          </button>
          <button onClick={() => setShowNew(true)}
            disabled={!status?.ok}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="new-payment-btn">
            <Plus size={16}/> Nuovo pagamento
          </button>
        </div>
      </div>

      {/* Status banner */}
      {status && (
        <div className={`p-4 rounded-xl border ${status.ok ? "bg-green-900/20 border-green-700" : "bg-red-900/20 border-red-700"}`} data-testid="sumup-status">
          {status.ok ? (
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-2 text-green-400 font-semibold">
                <CheckCircle2 size={16}/> SumUp LIVE
              </span>
              <span>·</span>
              <span><b>{status.merchant_name?.slice(0, 50) || "?"}...</b></span>
              <span>·</span>
              <span className="text-zinc-400">Merchant: <b className="text-white">{status.merchant_code}</b></span>
              <span>·</span>
              <span className="text-zinc-400">{status.country} · {status.currency}</span>
            </div>
          ) : (
            <div className="text-red-400 text-sm">
              ⚠ SumUp NON connesso: {status.error}
            </div>
          )}
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
          <div className="text-xs text-zinc-400">Incassato</div>
          <div className="text-3xl font-bold text-green-400 mt-1">€ {totalIncassato.toFixed(2)}</div>
          <div className="text-xs text-zinc-500 mt-1">{payments.filter(p => ["PAID","SUCCESSFUL"].includes(p.status)).length} pagamenti</div>
        </div>
        <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
          <div className="text-xs text-zinc-400">In attesa</div>
          <div className="text-3xl font-bold text-amber-400 mt-1">€ {totalPending.toFixed(2)}</div>
          <div className="text-xs text-zinc-500 mt-1">{payments.filter(p => p.status === "PENDING").length} checkout pending</div>
        </div>
        <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
          <div className="text-xs text-zinc-400">Totale transazioni</div>
          <div className="text-3xl font-bold mt-1">{payments.length}</div>
          <div className="text-xs text-zinc-500 mt-1">dal gestionale</div>
        </div>
      </div>

      {/* Payments table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="font-semibold text-sm">Pagamenti recenti</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-4 py-2 text-left">Riferimento</th>
                <th className="px-4 py-2 text-left">Tipo</th>
                <th className="px-4 py-2 text-right">Importo</th>
                <th className="px-4 py-2 text-left">Stato</th>
                <th className="px-4 py-2 text-left">Descrizione</th>
                <th className="px-4 py-2 text-left">Data</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center py-8 text-zinc-500"><Loader2 className="animate-spin inline mr-2"/></td></tr>
              )}
              {!loading && payments.length === 0 && (
                <tr><td colSpan="7" className="text-center py-12 text-zinc-500">
                  <CreditCard className="mx-auto mb-2 text-zinc-700" size={32}/>
                  Nessun pagamento. Crea il primo checkout.
                </td></tr>
              )}
              {payments.map(p => {
                const s = STATUS_STYLE[p.status] || STATUS_STYLE.PENDING;
                const Icon = s.icon;
                return (
                  <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-800/50" data-testid={`payment-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">
                      <button onClick={() => copyToClipboard(p.sumup_checkout_reference, "Riferimento")}
                        className="hover:text-blue-400 flex items-center gap-1">
                        {p.sumup_checkout_reference}
                        <Copy size={11} className="opacity-40"/>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="px-2 py-0.5 rounded bg-zinc-800 capitalize">{p.payment_type}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold">€ {(p.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ color: s.color, background: s.bg }}>
                        <Icon size={11} className="inline mr-1"/>{s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400 max-w-xs truncate">{p.description}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => refreshOne(p.id)} title="Aggiorna stato live"
                        className="p-1.5 hover:bg-zinc-700 rounded" data-testid={`refresh-${p.id}`}>
                        <RefreshCw size={13}/>
                      </button>
                      {["PAID","SUCCESSFUL"].includes(p.status) && (
                        <button onClick={() => refund(p.id)} title="Rimborsa"
                          className="p-1.5 hover:bg-purple-900/40 text-purple-400 rounded" data-testid={`refund-${p.id}`}>
                          <RotateCcw size={13}/>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal nuovo pagamento */}
      {showNew && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Plus className="text-blue-500"/> Nuovo pagamento SumUp
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Importo (€)</label>
                <input type="number" step="0.01" min="0.50" autoFocus
                  value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  placeholder="0.50" className="w-full p-2 bg-zinc-950 border border-zinc-700 rounded"
                  data-testid="new-amount"/>
                <div className="text-[10px] text-zinc-500 mt-1">Minimo €0.50 · Valuta: EUR</div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Tipo pagamento</label>
                <select value={form.payment_type} onChange={e => setForm({...form, payment_type: e.target.value})}
                  className="w-full p-2 bg-zinc-950 border border-zinc-700 rounded"
                  data-testid="new-type">
                  <option value="cauzione">Cauzione contratto</option>
                  <option value="saldo">Saldo finale rientro</option>
                  <option value="generico">Pagamento generico</option>
                  <option value="extra">Extra / Danni</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Descrizione</label>
                <textarea rows="2" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="es. Cauzione veicolo AB123CD - Sig. Rossi"
                  className="w-full p-2 bg-zinc-950 border border-zinc-700 rounded text-sm"
                  data-testid="new-description"/>
              </div>
              <div className="bg-amber-900/20 border border-amber-700 rounded p-2 text-xs text-amber-300">
                ⚠ <b>SumUp LIVE</b>: il checkout creato genererà un pagamento <b>reale</b>. Il cliente completerà tramite POS fisico o link condiviso.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded">Annulla</button>
              <button onClick={createPayment} disabled={busy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded font-semibold disabled:opacity-50 flex items-center gap-2"
                data-testid="new-submit">
                {busy ? <Loader2 className="animate-spin" size={14}/> : <Plus size={14}/>}
                Crea checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
